/**
 * Navigation regroupée Gestionnaire + Joueur/Étudiant — BT-026.
 */
import { getLocaleLabel } from "@/coquille-bridge";
import {
  SENSEI_SHOTOKAN_BASE,
  SEMPAI_SHOTOKAN_BASE,
  LEGACY_SENSEI_SHOTOKAN_PATHS,
  LEGACY_SEMPAI_SHOTOKAN_PATHS,
} from "@/pages/shotokanBlackDragon/shotokanNavConfig.js";

function localizeItem(item, locale) {
  return { ...item, label: getLocaleLabel(item, locale) };
}

function pickItems(flatItems, paths, locale) {
  const byPath = new Map(flatItems.map((i) => [i.to, i]));
  return paths
    .map((p) => byPath.get(p))
    .filter(Boolean)
    .map((i) => localizeItem(i, locale));
}

function buildGroups(metaList, flatItems, locale) {
  return metaList
    .map((meta) => {
      const items = meta.customItems
        ? meta.customItems.map((i) => localizeItem(i, locale))
        : pickItems(flatItems, meta.paths || [], locale);

      return {
        id: meta.id,
        label: getLocaleLabel(meta, locale),
        labelFr: meta.labelFr,
        labelEn: meta.labelEn,
        icon: meta.icon,
        collapsible: meta.collapsible !== false,
        hubPath: meta.hubPath,
        matchPrefixes: meta.matchPrefixes,
        items,
      };
    })
    .filter((g) => g.items.length > 0);
}

function findGroupForPath(pathname, metaList) {
  for (const group of metaList) {
    if (group.id === "principal" || group.id === "dashboard") {
      if (group.paths?.some((p) => pathname === p || (p !== "/sensei" && p !== "/sempai" && p !== "/admin" && pathname.startsWith(`${p}/`)))) {
        return group.id;
      }
      if (group.paths?.includes(pathname)) return group.id;
      continue;
    }
    if (group.matchPrefixes?.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
      return group.id;
    }
    if (group.customItems) {
      const match = group.customItems.some((item) => {
        if (item.matchPrefixes) {
          return item.matchPrefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
        }
        const to = item.to;
        return item.end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
      });
      if (match) return group.id;
    }
    if (group.paths?.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
      return group.id;
    }
  }
  return null;
}

function mobileFromGroups(groups) {
  return groups.flatMap((g) => {
    if (!g.collapsible) return g.items;
    return [
      {
        id: `mobile-nav-${g.id}`,
        to: g.hubPath || g.items[0]?.to || "/",
        label: g.label,
        icon: g.icon || "•",
      },
    ];
  });
}

const SENSEI_SHOTOKAN_ITEMS = [
  {
    id: "sbd-sensei-overview",
    to: SENSEI_SHOTOKAN_BASE,
    end: true,
    labelFr: "Vue d'ensemble",
    labelEn: "Overview",
    icon: "🏠",
  },
  {
    id: "sbd-sensei-titres",
    to: `${SENSEI_SHOTOKAN_BASE}/titres`,
    labelFr: "Titres",
    labelEn: "Titles",
    icon: "🏅",
  },
  {
    id: "sbd-sensei-demandes",
    to: `${SENSEI_SHOTOKAN_BASE}/demandes`,
    labelFr: "Mes demandes",
    labelEn: "My requests",
    icon: "📋",
  },
  {
    id: "sbd-sensei-documents",
    to: `${SENSEI_SHOTOKAN_BASE}/documents`,
    labelFr: "Documents requis",
    labelEn: "Required documents",
    icon: "📄",
    matchPrefixes: [`${SENSEI_SHOTOKAN_BASE}/documents`, "/sensei/documents-titres"],
  },
];

const SEMPAI_SHOTOKAN_ITEMS = [
  {
    id: "sbd-sempai-overview",
    to: SEMPAI_SHOTOKAN_BASE,
    end: true,
    labelFr: "Vue d'ensemble",
    labelEn: "Overview",
    icon: "🏠",
  },
  {
    id: "sbd-sempai-parcours",
    to: `${SEMPAI_SHOTOKAN_BASE}/parcours`,
    labelFr: "Parcours titres",
    labelEn: "Title path",
    icon: "🎖️",
    matchPrefixes: ["/sempai/titres"],
  },
  {
    id: "sbd-sempai-documents",
    to: `${SEMPAI_SHOTOKAN_BASE}/documents`,
    labelFr: "Documents requis",
    labelEn: "Required documents",
    icon: "📄",
    matchPrefixes: [`${SEMPAI_SHOTOKAN_BASE}/documents`, "/sempai/documents-titres"],
  },
];

const SENSEI_FLAT_FALLBACK = [
  { id: "connected-home", to: "/accueil", labelFr: "Accueil", labelEn: "Home", icon: "🏠", end: true },
  { id: "manager-dashboard", to: "/sensei", labelFr: "Tableau de bord", labelEn: "Dashboard", icon: "📊", end: true },
  { id: "common-training", to: "/formation", labelFr: "Formation", labelEn: "Training", icon: "🎓" },
  { id: "manager-dojos", to: "/sensei/mon-dojo", labelFr: "Mon dojo", labelEn: "My dojo", icon: "🥋" },
  { id: "manager-classes", to: "/sensei/classes", labelFr: "Classes / cours", labelEn: "Classes / courses", icon: "📅" },
  { id: "manager-events", to: "/sensei/evenements", labelFr: "Événements", labelEn: "Events", icon: "🗓️" },
  { id: "manager-attendance", to: "/sensei/presences", labelFr: "Présences légères", labelEn: "Light attendance", icon: "✓" },
  { id: "manager-students", to: "/sensei/etudiants", labelFr: "Étudiants", labelEn: "Students", icon: "🎓" },
  { id: "manager-corrections", to: "/sensei/corrections", labelFr: "Corrections / demandes", labelEn: "Reviews", icon: "✅" },
  { id: "manager-progress", to: "/sensei/progression", labelFr: "Progression", labelEn: "Progress", icon: "📈" },
  { id: "manager-exams", to: "/sensei/examens", labelFr: "Examens légers", labelEn: "Light exams", icon: "📝" },
  { id: "manager-belts", to: "/sensei/ceintures", labelFr: "Ceintures / grades", labelEn: "Belts / grades", icon: "🎗️" },
  { id: "manager-documents", to: "/sensei/documents", labelFr: "Documents", labelEn: "Documents", icon: "📄" },
  { id: "common-help", to: "/aide", labelFr: "Aide", labelEn: "Help", icon: "❓" },
];

const SEMPAI_FLAT_FALLBACK = [
  { id: "connected-home", to: "/accueil", labelFr: "Accueil", labelEn: "Home", icon: "🏠", end: true },
  { id: "assistant-dashboard", to: "/sempai", labelFr: "Tableau de bord", labelEn: "Dashboard", icon: "📊", end: true },
  { id: "common-training", to: "/formation", labelFr: "Formation", labelEn: "Training", icon: "🎓" },
  { id: "assistant-classes", to: "/sempai/classes", labelFr: "Classes / cours", labelEn: "Classes / courses", icon: "📅" },
  { id: "assistant-events", to: "/sempai/evenements", labelFr: "Événements", labelEn: "Events", icon: "🗓️" },
  { id: "assistant-attendance", to: "/sempai/presences", labelFr: "Présences légères", labelEn: "Light attendance", icon: "✓" },
  { id: "assistant-students", to: "/sempai/etudiants", labelFr: "Étudiants", labelEn: "Students", icon: "🎓" },
  { id: "assistant-progress", to: "/sempai/progression", labelFr: "Progression", labelEn: "Progress", icon: "📈" },
  { id: "assistant-belts", to: "/sempai/ceintures", labelFr: "Ceintures", labelEn: "Belts", icon: "🎗️" },
  { id: "assistant-documents", to: "/sempai/documents", labelFr: "Documents", labelEn: "Documents", icon: "📄" },
  { id: "common-help", to: "/aide", labelFr: "Aide", labelEn: "Help", icon: "❓" },
];

const MANAGER_GROUP_META = [
  {
    id: "principal",
    labelFr: "Principal",
    labelEn: "Main",
    icon: "🏠",
    collapsible: false,
    paths: ["/accueil", "/sensei", "/formation"],
  },
  {
    id: "mon-dojo",
    labelFr: "Mon dojo",
    labelEn: "My dojo",
    icon: "🥋",
    collapsible: false,
    paths: ["/sensei/mon-dojo"],
  },
  {
    id: "mes-cours",
    labelFr: "Mes cours",
    labelEn: "My classes",
    icon: "📅",
    paths: ["/sensei/classes", "/sensei/evenements", "/sensei/presences"],
  },
  {
    id: "mes-eleves",
    labelFr: "Mes élèves",
    labelEn: "My students",
    icon: "🎓",
    paths: ["/sensei/etudiants", "/sensei/corrections"],
  },
  {
    id: "progression",
    labelFr: "Progression",
    labelEn: "Progress",
    icon: "📈",
    paths: ["/sensei/progression", "/sensei/examens", "/sensei/ceintures"],
  },
  {
    id: "shotokan",
    labelFr: "Shotokan Black Dragon",
    labelEn: "Shotokan Black Dragon",
    icon: "🐉",
    customItems: SENSEI_SHOTOKAN_ITEMS,
    matchPrefixes: [SENSEI_SHOTOKAN_BASE, ...LEGACY_SENSEI_SHOTOKAN_PATHS],
  },
  {
    id: "documents",
    labelFr: "Documents utiles",
    labelEn: "Useful documents",
    icon: "📄",
    collapsible: false,
    paths: ["/sensei/documents"],
  },
  {
    id: "aide",
    labelFr: "Aide",
    labelEn: "Help",
    icon: "❓",
    collapsible: false,
    paths: ["/aide"],
  },
];

const ASSISTANT_GROUP_META = [
  {
    id: "principal",
    labelFr: "Principal",
    labelEn: "Main",
    icon: "🏠",
    collapsible: false,
    paths: ["/accueil", "/sempai", "/formation"],
  },
  {
    id: "classes-assistees",
    labelFr: "Classes assistées",
    labelEn: "Assisted classes",
    icon: "📅",
    paths: ["/sempai/classes", "/sempai/evenements", "/sempai/presences"],
  },
  {
    id: "etudiants",
    labelFr: "Étudiants",
    labelEn: "Students",
    icon: "🎓",
    collapsible: false,
    paths: ["/sempai/etudiants"],
  },
  {
    id: "progression",
    labelFr: "Progression consultation",
    labelEn: "Progression review",
    icon: "📈",
    paths: ["/sempai/progression", "/sempai/ceintures"],
  },
  {
    id: "shotokan",
    labelFr: "Shotokan Black Dragon",
    labelEn: "Shotokan Black Dragon",
    icon: "🐉",
    customItems: SEMPAI_SHOTOKAN_ITEMS,
    matchPrefixes: [SEMPAI_SHOTOKAN_BASE, ...LEGACY_SEMPAI_SHOTOKAN_PATHS],
  },
  {
    id: "documents",
    labelFr: "Documents utiles",
    labelEn: "Useful documents",
    icon: "📄",
    collapsible: false,
    paths: ["/sempai/documents"],
  },
  {
    id: "aide",
    labelFr: "Aide",
    labelEn: "Help",
    icon: "❓",
    collapsible: false,
    paths: ["/aide"],
  },
];

/** Navigation étudiant — onglets simples BT-026. */
const PLAYER_NAV_META = [
  { id: "connected-home", to: "/accueil", labelFr: "Accueil", labelEn: "Home", icon: "🏠", end: true },
  { id: "player-dashboard", to: "/mon-espace", labelFr: "Mon espace", labelEn: "My space", icon: "🏠", end: true },
  { id: "player-classes", to: "/mes-cours", labelFr: "Mes cours", labelEn: "My classes", icon: "📅" },
  { id: "player-events", to: "/mes-evenements", labelFr: "Mes événements", labelEn: "My events", icon: "🗓️" },
  { id: "player-progress", to: "/ma-progression", labelFr: "Ma progression", labelEn: "My progress", icon: "📈" },
  { id: "player-exams", to: "/mes-examens", labelFr: "Mes examens", labelEn: "My exams", icon: "📝" },
  { id: "player-calendar", to: "/mon-calendrier", labelFr: "Mon calendrier", labelEn: "My calendar", icon: "🗓️" },
  { id: "player-adhesion", to: "/mon-adhesion", labelFr: "Mon adhésion", labelEn: "My membership", icon: "🪪" },
  { id: "player-bd", to: "/espace-black-dragon", labelFr: "Black Dragon", labelEn: "Black Dragon", icon: "🐉" },
  { id: "player-titles", to: "/mes-titres", labelFr: "Mes titres", labelEn: "My titles", icon: "🏅" },
  { id: "player-documents", to: "/mes-documents", labelFr: "Mes documents", labelEn: "My documents", icon: "📄" },
  { id: "common-help", to: "/aide", labelFr: "Aide", labelEn: "Help", icon: "❓" },
];

/** Navigation parent — onglets simples BT-026. */
const PARENT_NAV_META = [
  { id: "connected-home", to: "/accueil", labelFr: "Accueil", labelEn: "Home", icon: "🏠", end: true },
  { id: "parent-dashboard", to: "/parent", labelFr: "Mes enfants", labelEn: "My children", icon: "👨‍👩‍👧", end: true },
  { id: "parent-schedule", to: "/parent/horaires", labelFr: "Horaires", labelEn: "Schedule", icon: "🕐" },
  { id: "parent-events", to: "/parent/evenements", labelFr: "Événements", labelEn: "Events", icon: "📅" },
  { id: "parent-progress", to: "/parent/progression", labelFr: "Progression", labelEn: "Progress", icon: "📈" },
  { id: "parent-adhesion", to: "/parent/adhesion", labelFr: "Adhésion", labelEn: "Membership", icon: "🪪" },
  { id: "parent-documents", to: "/parent/documents", labelFr: "Documents", labelEn: "Documents", icon: "📄" },
  { id: "parent-comms", to: "/parent/communications", labelFr: "Communications", labelEn: "Communications", icon: "💬" },
  { id: "common-help", to: "/aide", labelFr: "Aide", labelEn: "Help", icon: "❓" },
];

export function getManagerNavGroups(locale = "fr", flatItems = []) {
  const flat = flatItems.length ? flatItems : SENSEI_FLAT_FALLBACK.map((i) => localizeItem(i, locale));
  return buildGroups(MANAGER_GROUP_META, flat, locale);
}

export function getManagerSidebarItems(locale = "fr", flatItems = []) {
  return getManagerNavGroups(locale, flatItems).flatMap((g) => g.items);
}

export function getManagerMobileNavItems(locale = "fr", flatItems = []) {
  return mobileFromGroups(getManagerNavGroups(locale, flatItems));
}

export function findManagerNavGroupForPath(pathname) {
  return findGroupForPath(pathname, MANAGER_GROUP_META);
}

export function getAssistantNavGroups(locale = "fr", flatItems = []) {
  const flat = flatItems.length ? flatItems : SEMPAI_FLAT_FALLBACK.map((i) => localizeItem(i, locale));
  return buildGroups(ASSISTANT_GROUP_META, flat, locale);
}

export function getAssistantSidebarItems(locale = "fr", flatItems = []) {
  return getAssistantNavGroups(locale, flatItems).flatMap((g) => g.items);
}

export function getAssistantMobileNavItems(locale = "fr", flatItems = []) {
  return mobileFromGroups(getAssistantNavGroups(locale, flatItems));
}

export function findAssistantNavGroupForPath(pathname) {
  return findGroupForPath(pathname, ASSISTANT_GROUP_META);
}

export function getPlayerTabItems(locale = "fr") {
  return PLAYER_NAV_META.map((i) => localizeItem(i, locale));
}

export function getParentNavItems(locale = "fr") {
  return PARENT_NAV_META.map((i) => localizeItem(i, locale));
}

export {
  MANAGER_GROUP_META,
  ASSISTANT_GROUP_META,
  PLAYER_NAV_META,
  PARENT_NAV_META,
};
